/**
 * Ascensos de Grado Report
 * Minister degree changes with promotions summary.
 */

import { useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, ArrowRight } from "lucide-react";
import { type ColumnDef } from "@tanstack/react-table";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { AnimatedCard } from "@/components/ui/animated-card";
import { AnimatedNumber } from "@/components/ui/animated-number";
import { DateRangeFilter } from "@/components/atoms/filters/DateRangeFilter";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { DataColumnHeader } from "@/components/molecules/table";
import {
  SelfContainedDataTable,
  selectedIncludesFn,
} from "@/components/organisms/SelfContainedDataTable";
import { BarChartAnimated } from "@/components/ui/bar-chart-animated";
import { useMinisterDegreeChangesReport } from "../hooks/useMinisterDegreeChangesReport";
import { ExportDropdown } from "../components/ExportDropdown";
import type { ExportColumn } from "../utils/exportExcel";
import type { MinisterDegreeChangeEntry } from "../types";

const columns: ColumnDef<MinisterDegreeChangeEntry>[] = [
  {
    accessorKey: "adb_code",
    header: ({ column }) => <DataColumnHeader column={column} title="Código ADB" />,
    filterFn: selectedIncludesFn,
    cell: ({ row }) => <span className="font-mono text-xs">{row.original.adb_code ?? "—"}</span>,
  },
  {
    accessorKey: "minister_name",
    header: ({ column }) => <DataColumnHeader column={column} title="Ministro" />,
    filterFn: selectedIncludesFn,
    cell: ({ row }) => <span className="font-medium">{row.original.minister_name}</span>,
  },
  {
    accessorKey: "current_district_name",
    header: ({ column }) => <DataColumnHeader column={column} title="Distrito actual" />,
    filterFn: selectedIncludesFn,
    cell: ({ row }) => row.original.current_district_name ?? "—",
  },
  {
    accessorKey: "current_region_name",
    header: ({ column }) => <DataColumnHeader column={column} title="Región actual" />,
    filterFn: selectedIncludesFn,
    cell: ({ row }) => row.original.current_region_name ?? "—",
  },
  {
    accessorKey: "degree_from_name",
    header: ({ column }) => <DataColumnHeader column={column} title="Grado origen" />,
    filterFn: selectedIncludesFn,
    cell: ({ row }) => (
      <span className="text-muted-foreground">{row.original.degree_from_name ?? "—"}</span>
    ),
  },
  {
    id: "arrow",
    header: "",
    enableSorting: false,
    enableColumnFilter: false,
    cell: () => <ArrowRight className="size-3 text-muted-foreground" />,
  },
  {
    accessorKey: "degree_to_name",
    header: ({ column }) => <DataColumnHeader column={column} title="Grado destino" />,
    filterFn: selectedIncludesFn,
    cell: ({ row }) => (
      <span className="font-medium">{row.original.degree_to_name ?? "—"}</span>
    ),
  },
  {
    accessorKey: "event_date",
    header: ({ column }) => <DataColumnHeader column={column} title="Fecha" />,
    filterFn: selectedIncludesFn,
    cell: ({ row }) => new Date(row.original.event_date).toLocaleDateString("es-BO"),
  },
  {
    accessorKey: "notes",
    header: "Notas",
    enableSorting: false,
    enableColumnFilter: false,
    cell: ({ row }) => (
      <span className="block max-w-[180px] truncate text-muted-foreground" title={row.original.notes ?? undefined}>
        {row.original.notes ?? "—"}
      </span>
    ),
  },
];

const EXPORT_COLUMNS: ExportColumn<MinisterDegreeChangeEntry>[] = [
  { label: "Código ADB",      getValue: (r) => r.adb_code ?? "" },
  { label: "Ministro",        getValue: (r) => r.minister_name },
  { label: "Distrito Actual", getValue: (r) => r.current_district_name ?? "" },
  { label: "Región Actual",   getValue: (r) => r.current_region_name ?? "" },
  { label: "Grado Origen",    getValue: (r) => r.degree_from_name ?? "" },
  { label: "Grado Destino",   getValue: (r) => r.degree_to_name ?? "" },
  { label: "Fecha",           getValue: (r) => new Date(r.event_date).toLocaleDateString("es-BO") },
  { label: "Notas",           getValue: (r) => r.notes ?? "" },
];

export function MinisterDegreeChangesReportPage() {
  const navigate = useNavigate();

  const { changes, degreeToSummary, filters, isLoading, error, updateFilter, resetFilters } =
    useMinisterDegreeChangesReport();

  const [filteredRows, setFilteredRows] = useState<MinisterDegreeChangeEntry[]>([]);
  const [isFiltered, setIsFiltered] = useState(false);

  const handleFilteredDataChange = useCallback(
    (rows: MinisterDegreeChangeEntry[], filtered: boolean) => {
      setFilteredRows(rows);
      setIsFiltered(filtered);
    },
    []
  );

  const filterPanel = (
    <div className="flex flex-wrap items-end gap-3 p-3">
      <DateRangeFilter
        dateFrom={filters.dateFrom}
        dateTo={filters.dateTo}
        onDateFromChange={(v) => updateFilter("dateFrom", v)}
        onDateToChange={(v) => updateFilter("dateTo", v)}
        label="Período"
      />
      <Button variant="outline" size="sm" onClick={resetFilters}>Limpiar</Button>
    </div>
  );

  if (error) {
    return (
      <div className="space-y-4 p-6">
        <Button variant="ghost" onClick={() => navigate("/reports")}>
          <ArrowLeft className="mr-2 size-4" />Volver a Reportes
        </Button>
        <div className="flex h-64 items-center justify-center rounded-lg border border-destructive/20 bg-destructive/5">
          <p className="text-destructive">Error al cargar el reporte</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 p-4 md:p-6">
      {/* Header */}
      <div>
        <Button variant="ghost" onClick={() => navigate("/reports")} className="mb-4">
          <ArrowLeft className="mr-2 size-4" />Volver a Reportes
        </Button>
        <h1 className="text-3xl font-bold">Ascensos de Grado</h1>
        <p className="mt-2 text-muted-foreground">
          Cambios de grado ministerial registrados
        </p>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
        <AnimatedCard variant="gradient" className="border-0">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-semibold text-muted-foreground">Total cambios</CardTitle>
          </CardHeader>
          <CardContent>
            {isLoading ? <div className="h-8 w-16 animate-pulse rounded bg-muted" /> : (
              <AnimatedNumber value={changes.length} className="text-2xl font-bold md:text-3xl" />
            )}
          </CardContent>
        </AnimatedCard>
        <AnimatedCard variant="gradient" className="col-span-2 border-0 sm:col-span-2">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-semibold text-muted-foreground">Período</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm font-medium">{filters.dateFrom} → {filters.dateTo}</p>
          </CardContent>
        </AnimatedCard>
      </div>

      {/* Degree changes bar chart */}
      {!isLoading && degreeToSummary.length > 0 && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Ascensos por Grado Destino</CardTitle>
          </CardHeader>
          <CardContent>
            <BarChartAnimated
              data={degreeToSummary.map((row) => ({
                name: row.degree_to_name,
                value: row.count,
              }))}
              height={240}
            />
          </CardContent>
        </Card>
      )}

      {/* Degree promotion summary */}
      {!isLoading && degreeToSummary.length > 0 && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Resumen por Grado Destino</CardTitle>
          </CardHeader>
          <CardContent className="overflow-x-auto p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Grado destino</TableHead>
                  <TableHead className="w-[120px] text-center">Cambios</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {degreeToSummary.map((row) => (
                  <TableRow key={row.degree_to_id}>
                    <TableCell className="font-medium">{row.degree_to_name}</TableCell>
                    <TableCell className="text-center tabular-nums font-semibold">{row.count}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {/* Changes table */}
      <SelfContainedDataTable
        data={changes}
        columns={columns}
        isLoading={isLoading}
        getSearchableText={(row) => {
          const adbCode = row.adb_code ?? "";
          return `${adbCode} ${adbCode.replace(/\D/g, "")} ${row.minister_name ?? ""}`;
        }}
        searchPlaceholder="Buscar por nombre o código ADB..."
        initialSorting={[{ id: "minister_name", desc: false }]}
        filterPanel={filterPanel}
        emptyMessage="No se encontraron cambios de grado para el período seleccionado"
        defaultPageSize={50}
        pageSizeOptions={[20, 50, 100]}
        onFilteredDataChange={handleFilteredDataChange}
        toolbarActions={
          <ExportDropdown
            rows={filteredRows}
            isFiltered={isFiltered}
            filename={`ascensos-grado-${filters.dateFrom}_${filters.dateTo}`}
            columns={EXPORT_COLUMNS}
            getDistrictName={(r) => r.current_district_name ?? null}
            getRegionName={(r) => r.current_region_name ?? null}
            getSortName={(r) => r.minister_name ?? ""}
            disabled={isLoading}
          />
        }
      />
    </div>
  );
}
